import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const StarRow = ({ value, size = 14, interactive = false, onChange }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => {
      const filled = value >= i;
      const half = !filled && value > i - 1 && value < i;
      return (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange?.(i)}
          className={`relative ${interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
          style={{ width: size, height: size }}
          aria-label={`${i} stars`}
        >
          <Star className="absolute inset-0 text-neutral-700" style={{ width: size, height: size }} />
          {(filled || half) && (
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: filled ? "100%" : `${(value - (i - 1)) * 100}%` }}
            >
              <Star
                className="fill-[#FFC300] text-[#FFC300]"
                style={{ width: size, height: size }}
              />
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const initialForm = { customer_name: "", rating: 5, comment: "", location: "" };

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [hover, setHover] = useState(0);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get("/reviews?limit=20"), api.get("/reviews/stats")]);
      setReviews(r.data);
      setStats(s.data);
    } catch {
      // silent — section can render empty
    }
  };

  useEffect(() => {
    load();
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.customer_name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    if (form.comment.trim().length < 10) {
      toast.error("Please write at least a short review (10+ characters)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you for your review!");
      setForm(initialForm);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit review";
      toast.error(typeof msg === "string" ? msg : "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reviews"
      className="relative bg-black py-16 sm:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-10 sm:mb-14">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFC300] font-bold">
              04 · Reviews
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-none">
              What Our<br />
              <span className="text-[#FFC300]">Customers Say</span>
            </h2>
          </div>
          <div className="lg:text-right">
            <div className="flex items-center gap-3 lg:justify-end">
              <StarRow value={stats.average || 5} size={18} />
              <div className="font-display text-2xl text-white">
                {stats.count > 0 ? stats.average.toFixed(1) : "—"}
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-1 uppercase tracking-[0.25em]">
              {stats.count} {stats.count === 1 ? "Review" : "Reviews"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Reviews list */}
          <div className="lg:col-span-7 space-y-4">
            {reviews.length === 0 ? (
              <div className="border border-white/10 p-8 sm:p-10 text-center" data-testid="no-reviews">
                <MessageSquareQuote className="w-8 h-8 text-[#FFC300] mx-auto mb-4" />
                <p className="text-neutral-400">
                  No reviews yet — be the first to share your experience.
                </p>
              </div>
            ) : (
              reviews.map((r, idx) => (
                <motion.article
                  key={r.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (idx % 4) * 0.06 }}
                  data-testid={`review-${r.id}`}
                  className="bg-[#0A0A0A] border border-[#1f1f1f] p-5 sm:p-6 hover:border-[#FFC300]/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="font-bold text-white">{r.customer_name}</div>
                      {r.location && (
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-0.5">
                          {r.location}
                        </div>
                      )}
                    </div>
                    <StarRow value={r.rating} size={14} />
                  </div>
                  <p className="text-sm text-neutral-300 leading-relaxed">"{r.comment}"</p>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-600 mt-3">
                    {new Date(r.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </motion.article>
              ))
            )}
          </div>

          {/* Submit form */}
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="lg:col-span-5 bg-[#0A0A0A] border border-[#262626] p-6 sm:p-8 lg:p-10 h-fit lg:sticky lg:top-24"
          >
            <h3 className="font-display text-2xl uppercase font-black tracking-tighter mb-2">
              Leave a <span className="text-[#FFC300]">Review</span>
            </h3>
            <p className="text-sm text-neutral-400 mb-6">
              Loved your ride? Share your experience to help other travelers.
            </p>

            <label className="block mb-5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-1">
                Your Name *
              </span>
              <input
                type="text"
                required
                data-testid="review-name-input"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="John D."
                maxLength={80}
              />
            </label>

            <label className="block mb-5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-1">
                Where From (optional)
              </span>
              <input
                type="text"
                data-testid="review-location-input"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="London, UK"
                maxLength={80}
              />
            </label>

            <div className="mb-5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-2">
                Your Rating *
              </span>
              <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setHover(i)}
                    onClick={() => setForm({ ...form, rating: i })}
                    data-testid={`review-star-${i}`}
                    className="hover:scale-110 transition-transform"
                    aria-label={`Rate ${i} stars`}
                  >
                    <Star
                      className={`w-7 h-7 ${
                        (hover || form.rating) >= i
                          ? "fill-[#FFC300] text-[#FFC300]"
                          : "text-neutral-700"
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-sm font-bold text-[#FFC300]">{hover || form.rating}/5</span>
              </div>
            </div>

            <label className="block mb-6">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-1">
                Your Review *
              </span>
              <textarea
                required
                rows={4}
                data-testid="review-comment-input"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors resize-none"
                placeholder="Great service, the driver was on time and very friendly..."
                maxLength={600}
                minLength={10}
              />
              <div className="text-[10px] text-neutral-600 mt-1 text-right">
                {form.comment.length}/600
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-review-btn"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-sm px-6 py-4 hover:bg-white disabled:opacity-60 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Submit Review"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
